import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ApiError } from '../api';
import { api, eventEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, formatWhen } from '../ui';
import { INPUT, dollars, inZone } from './event/shared';
import { PackageImportPanel } from './migration/PackageImport';
import { HistoryImportPanel } from './migration/HistoryImport';
import { dateWords } from '../terms';

/**
 * Importing tickets sold in a previous system (D-158).
 *
 * See docs/migration-nutcracker.md. The screen is built around one rule: **you
 * read the dry run before anything is written.** The import button does not
 * exist until a preview has come back clean, and a preview that found any
 * problem at all offers nothing but the problems.
 *
 * That is not caution for its own sake. An import issues real admissions and
 * real ledger entries in bulk, and once the holders have been emailed there is
 * no undoing it.
 */

/** A run that has happened, and whether its holders have been told (D-177). */
interface MigrationImport {
  id: string;
  event_title: string;
  starts_at: string;
  timezone: string;
  orders_count: number;
  tickets_count: number;
  total_cents: number;
  failed_on: string | null;
  imported_at: string;
  imported_by: string | null;
  notified_at: string | null;
  notified_count: number;
  notified_by: string | null;
  unnotified_orders: number;
}

interface PreviewOrder {
  buyer: string;
  email: string;
  phone: string | null;
  seats: string[];
  tiers: string[];
  total_cents: number;
}

interface Preview {
  performance: { id: string; event_title: string; starts_at: string };
  orders: PreviewOrder[];
  skipped: { buyer: string; reason: string }[];
  problems: { code: string; message: string; line?: number }[];
  totals: { orders: number; tickets: number; face_cents: number; fee_cents: number; total_cents: number };
  ready: boolean;
}

interface ImportResult {
  imported: { order_number: string; buyer: string; seats: string[]; total_cents: number }[];
  failures: { buyer: string; message: string }[];
  totals: { orders: number; tickets: number; total_cents: number };
}

const readFile = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsText(file);
  });

export default function MigrationPage() {
  const can = usePermissions();
  const [eventId, setEventId] = useState('');
  const [performanceId, setPerformanceId] = useState('');
  const [seatCsv, setSeatCsv] = useState<{ name: string; text: string } | null>(null);
  const [buyerCsv, setBuyerCsv] = useState<{ name: string; text: string } | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const events = useQuery({ queryKey: ['events', 'PUBLISHED'], queryFn: () => eventEndpoints.list('PUBLISHED') });
  const detail = useQuery({ queryKey: ['event', eventId], queryFn: () => eventEndpoints.detail(eventId), enabled: Boolean(eventId) });

  // A preview and an import send exactly the same body. The only difference is
  // which endpoint, which is why what you read is what happens.
  const body = () => ({ performance_id: performanceId, seat_csv: seatCsv?.text ?? '', buyer_csv: buyerCsv?.text ?? '' });

  const runPreview = useMutation({
    mutationFn: () => api.post<Preview>('/api/admin/migration/preview', body()),
    onSuccess: (data) => {
      setPreview(data);
      setResult(null);
      setError(null);
    },
    onError: (cause: ApiError) => {
      setPreview(null);
      setError(cause.message);
    },
  });

  const runImport = useMutation({
    mutationFn: () => api.post<ImportResult>('/api/admin/migration/import', body()),
    onSuccess: (data) => {
      setResult(data);
      setPreview(null);
      setError(null);
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  if (!can('migration.import')) return <NotPermitted permission="migration.import" />;

  const performances = detail.data?.performances ?? [];
  const canPreview = Boolean(performanceId && seatCsv && buyerCsv);
  const busy = runPreview.isPending || runImport.isPending;

  const pick = (set: (value: { name: string; text: string } | null) => void) => async (file: File | undefined) => {
    setPreview(null);
    setResult(null);
    if (!file) return set(null);
    try {
      set({ name: file.name, text: await readFile(file) });
    } catch (cause) {
      setError((cause as Error).message);
    }
  };

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}

      <Panel
        title="Import tickets sold in a previous system"
        description="Two exports from the old system: the box office file with the seats, and the admin file with the buyers and what they paid. They are joined on the buyer's name. Nothing is written until you have read the dry run."
      >
        <div className="space-y-4 px-5 py-4">
          <div className="flex flex-wrap items-end gap-4">
            <Field label="Event" htmlFor="mig-event">
              <select
                id="mig-event"
                value={eventId}
                onChange={(e) => {
                  setEventId(e.target.value);
                  setPerformanceId('');
                  setPreview(null);
                  setResult(null);
                }}
                className={INPUT}
              >
                <option value="">Choose an event…</option>
                {(events.data?.events ?? []).map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.title}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={`${dateWords().One}`} htmlFor="mig-performance">
              <select id="mig-performance" value={performanceId} onChange={(e) => { setPerformanceId(e.target.value); setPreview(null); setResult(null); }} className={INPUT} disabled={!eventId}>
                <option value="">Choose a date…</option>
                {performances.map((performance) => (
                  <option key={performance.id} value={performance.id}>
                    {new Date(performance.starts_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="flex flex-wrap gap-6">
            <label className="text-sm">
              <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Box office export (seats)</span>
              <input type="file" accept=".csv,text/csv" onChange={(e) => void pick(setSeatCsv)(e.target.files?.[0])} className="mt-1 block text-sm" />
              {seatCsv ? <span className="text-xs text-ink-400">{seatCsv.name}</span> : null}
            </label>
            <label className="text-sm">
              <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Admin export (buyers and amounts)</span>
              <input type="file" accept=".csv,text/csv" onChange={(e) => void pick(setBuyerCsv)(e.target.files?.[0])} className="mt-1 block text-sm" />
              {buyerCsv ? <span className="text-xs text-ink-400">{buyerCsv.name}</span> : null}
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button variant="primary" disabled={!canPreview || busy} onClick={() => runPreview.mutate()}>
              {runPreview.isPending ? 'Reading…' : 'Dry run'}
            </Button>
            {preview?.ready ? (
              <Button
                variant="danger"
                disabled={busy}
                onClick={() => {
                  if (window.confirm(`Import ${preview.totals.orders} orders and ${preview.totals.tickets} tickets for ${preview.performance.event_title}? This issues real tickets and cannot be undone once the holders have been emailed.`)) {
                    runImport.mutate();
                  }
                }}
              >
                {runImport.isPending ? 'Importing…' : `Import ${preview.totals.orders} orders`}
              </Button>
            ) : null}
            <p className="text-xs text-ink-400">The dry run writes nothing. The import button appears only when the whole file is clean.</p>
          </div>
        </div>
      </Panel>

      {preview && preview.problems.length > 0 ? (
        <Panel title={`${preview.problems.length} problem${preview.problems.length === 1 ? '' : 's'} — nothing will be imported`} description="Every one of these has to be fixed, in the old system or in the file, before any of this can be imported. A file is imported whole or not at all.">
          <Table headers={['Line', 'What is wrong']}>
            {preview.problems.map((problem, index) => (
              <tr key={`${problem.code}-${index}`} className="border-b border-line last:border-0">
                <td className="px-5 py-2 text-sm text-ink-400">{problem.line ?? '—'}</td>
                <td className="px-5 py-2 text-sm text-ink-900">
                  <Badge tone="bad">{problem.code.toLowerCase().replaceAll('_', ' ')}</Badge> {problem.message}
                </td>
              </tr>
            ))}
          </Table>
        </Panel>
      ) : null}

      {preview && preview.problems.length === 0 ? (
        <Panel
          title={`${preview.totals.orders} orders, ${preview.totals.tickets} tickets`}
          description={`${dollars(preview.totals.face_cents)} face + ${dollars(preview.totals.fee_cents)} fees = ${dollars(preview.totals.total_cents)}. The fees are the ones these customers were actually charged, not this event's fee rules. None of it is sent to QuickBooks.`}
        >
          <Table headers={['Buyer', 'Email', 'Seats', 'Tier', 'Paid']}>
            {preview.orders.map((order) => (
              <tr key={order.email + order.seats.join()} className="border-b border-line last:border-0">
                <td className="px-5 py-2 text-sm text-ink-900">{order.buyer}</td>
                <td className="px-5 py-2 text-sm text-ink-600">{order.email}</td>
                <td className="px-5 py-2 text-sm text-ink-600">{order.seats.join(', ')}</td>
                <td className="px-5 py-2 text-sm text-ink-600">{order.tiers.join(', ')}</td>
                <td className="tabular px-5 py-2 text-sm">{dollars(order.total_cents)}</td>
              </tr>
            ))}
          </Table>
          {preview.skipped.length > 0 ? (
            <div className="border-t border-line px-5 py-3">
              <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Skipped</p>
              <ul className="mt-1 space-y-1 text-sm text-ink-600">
                {preview.skipped.map((entry) => (
                  <li key={entry.buyer}>
                    {entry.buyer} — {entry.reason}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </Panel>
      ) : null}

      {result ? (
        <Panel title={`Imported ${result.totals.orders} orders, ${result.totals.tickets} tickets, ${dollars(result.totals.total_cents)}`} description="Check the totals against the old system before anybody is emailed. The migration email is sent as its own step.">
          {result.failures.length > 0 ? <ErrorNote>{result.failures.map((failure) => `${failure.buyer}: ${failure.message}`).join(' · ')}</ErrorNote> : null}
          <Table headers={['Order', 'Buyer', 'Seats', 'Paid']}>
            {result.imported.length === 0 ? (
              <EmptyRow columns={4}>Nothing was imported.</EmptyRow>
            ) : (
              result.imported.map((order) => (
                <tr key={order.order_number} className="border-b border-line last:border-0">
                  <td className="px-5 py-2 font-mono text-sm">{order.order_number}</td>
                  <td className="px-5 py-2 text-sm text-ink-900">{order.buyer}</td>
                  <td className="px-5 py-2 text-sm text-ink-600">{order.seats.join(', ')}</td>
                  <td className="tabular px-5 py-2 text-sm">{dollars(order.total_cents)}</td>
                </tr>
              ))
            )}
          </Table>
        </Panel>
      ) : null}

      <PackageImportPanel />

      <PreviousImports />

      <HistoryImportPanel />
    </div>
  );
}

/**
 * Every import that has been run, and the step that finishes it (D-177).
 *
 * The import writes the tickets; **this** is what tells the people holding
 * them. Until it has been pressed, twenty-one people have valid tickets in a
 * system they have never heard of and no idea their old ones are dead — so the
 * state is shown on every row rather than being something somebody has to
 * remember.
 *
 * The button is enabled while anybody on the run is still unemailed and
 * disabled once they all are. It is safe to press twice: the email is keyed on
 * the order, so a second press sends only what a first one missed.
 */
function PreviousImports() {
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const imports = useQuery<{ imports: MigrationImport[] }, ApiError>({ queryKey: ['migration-imports'], queryFn: () => api.get('/api/admin/migration/imports') });

  const notify = useMutation({
    mutationFn: (id: string) => api.post<{ queued: number; already: number }>(`/api/admin/migration/imports/${id}/notify`, {}),
    onSuccess: (result) => {
      setError(null);
      setNotice(`${result.queued} email${result.queued === 1 ? '' : 's'} queued with the new tickets attached${result.already > 0 ? `; ${result.already} had already been sent` : ''}.`);
      void imports.refetch();
    },
    onError: (cause: ApiError) => {
      setNotice(null);
      setError(cause.message);
    },
  });

  const rows = imports.data?.imports ?? [];
  if (imports.isPending) return null;

  return (
    <Panel
      title="Completed imports"
      description="Every run, and whether the ticket holders have been emailed. The email carries their new tickets as a PDF and tells them the old ones no longer work."
    >
      {error ? (
        <div className="px-5 pt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      ) : null}
      {notice ? <p className="mx-5 mt-3 rounded border border-ok/30 bg-ok-soft px-3 py-2 text-sm text-ok">{notice}</p> : null}
      <Table headers={['Imported', `${dateWords().One}`, 'Orders', 'Tickets', 'Value', 'Holders told', '']}>
        {rows.length === 0 ? (
          <EmptyRow columns={7}>Nothing has been imported yet.</EmptyRow>
        ) : (
          rows.map((run) => (
            <tr key={run.id} className="border-b border-line align-top last:border-0">
              <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">
                {formatWhen(run.imported_at)}
                {run.imported_by ? <span className="block text-xs text-ink-400">{run.imported_by}</span> : null}
              </td>
              <td className="px-5 py-3 text-sm text-ink-900">
                {run.event_title}
                <span className="block text-xs text-ink-400">{inZone(run.starts_at, run.timezone)}</span>
                {run.failed_on ? <span className="block text-xs text-bad">stopped on {run.failed_on}</span> : null}
              </td>
              <td className="tabular px-5 py-3 text-sm">{run.orders_count}</td>
              <td className="tabular px-5 py-3 text-sm">{run.tickets_count}</td>
              <td className="tabular px-5 py-3 text-sm">{dollars(run.total_cents)}</td>
              <td className="px-5 py-3 text-sm">
                {run.notified_at ? (
                  <>
                    <Badge tone="ok">sent</Badge>
                    <span className="mt-0.5 block text-xs text-ink-400">
                      {run.notified_count} on {formatWhen(run.notified_at)}
                      {run.notified_by ? ` by ${run.notified_by}` : ''}
                    </span>
                  </>
                ) : (
                  <Badge tone="warn">not told</Badge>
                )}
                {run.notified_at && run.unnotified_orders > 0 ? (
                  <span className="mt-0.5 block text-xs text-warn">{run.unnotified_orders} still to send</span>
                ) : null}
              </td>
              <td className="px-5 py-3 text-right">
                <Button
                  variant="primary"
                  // Enabled while anybody is still unemailed, whether or not the
                  // button has been pressed before: a run that gained orders on
                  // a second attempt still has people to tell.
                  disabled={run.unnotified_orders === 0 || notify.isPending}
                  onClick={() => {
                    if (window.confirm(`Email ${run.unnotified_orders} ticket holder${run.unnotified_orders === 1 ? '' : 's'} for ${run.event_title}?\n\nEach one gets their new tickets attached and is told the old ones no longer work. This cannot be taken back.`)) {
                      notify.mutate(run.id);
                    }
                  }}
                >
                  {run.unnotified_orders === 0 ? 'All told' : `Send ${run.unnotified_orders}`}
                </Button>
              </td>
            </tr>
          ))
        )}
      </Table>
    </Panel>
  );
}
